import { NetworkStatus, useMutation, useQuery } from '@apollo/client'

import { gql } from '@/clients/utoniq-core'
import {
  BlockUser,
  BlockUserMutation,
  BlockUserMutationVariables,
  ConsolePostDetailQuery,
  ConsolePostDetailQueryVariables,
  DeletePostMutation,
  DeletePostMutationVariables,
  DeletePostReply,
  DeletePostReplyMutation,
  DeletePostReplyMutationVariables,
  LikePost,
  LikePostMutation,
  LikePostMutationVariables,
  PostBoxFragmentDoc,
  RepliesConnectionFragmentDoc,
  ReplyPost,
  ReplyPostMutation,
  ReplyPostMutationVariables,
  UnlikePost,
  UnlikePostMutation,
  UnlikePostMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { openReportForm } from '@/core/googleForm'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter<{ postId: string }>()
  const { user } = useCurrentUser()

  const { data, networkStatus, fetchMore, refetch } = useQuery<
    ConsolePostDetailQuery,
    ConsolePostDetailQueryVariables
  >(QUERY, {
    variables: {
      postId: router.query.postId,
    },
  })

  const [replyPost] = useMutation<
    ReplyPostMutation,
    ReplyPostMutationVariables
  >(ReplyPost)
  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BlockUser)
  const [deletePost, { loading: loadingDeletePost }] = useMutation<
    DeletePostMutation,
    DeletePostMutationVariables
  >(DELETE_POST, {
    onCompleted() {
      router.goBack(routerPages.ConsolePost)
    },
  })
  const [deletePostReply] = useMutation<
    DeletePostReplyMutation,
    DeletePostReplyMutationVariables
  >(DeletePostReply)
  const [unlikePost] = useMutation<
    UnlikePostMutation,
    UnlikePostMutationVariables
  >(UnlikePost)
  const [likePost] = useMutation<LikePostMutation, LikePostMutationVariables>(
    LikePost
  )
  return {
    state: {
      artistId: user?.uid ?? '',
      loading:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.fetchMore,
      loadingDeletePost,
      hasNextPage: !!data?.post?.replies?.pageInfo.hasNextPage,
      post: data?.post ?? null,
      user: data?.user ?? null,
    },
    actions: {
      async onLike(isLike, likeCount) {
        if (isLike) {
          await unlikePost({
            variables: { unlikePostPostId: router.query.postId },
            optimisticResponse: ({ unlikePostPostId }) => ({
              unlikePost: {
                __typename: 'Post',
                id: unlikePostPostId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likePost({
          variables: { likePostPostId: router.query.postId },
          optimisticResponse: ({ likePostPostId }) => ({
            likePost: {
              __typename: 'Post',
              id: likePostPostId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
      async onReply({ content }) {
        await replyPost({
          variables: {
            replyPostInputs: {
              postId: router.query.postId,
              content,
            },
          },
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            postId: router.query.postId,
            pageInput: {
              cursor: data?.post?.replies?.pageInfo.endCursor || null,
            },
          },
        })
      },

      async onDeleteReply(replyId) {
        await deletePostReply({
          variables: {
            deletePostReplyId: replyId,
          },
        })
      },
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: user?.email ?? '',
          contentType: 'post-replies',
          contentId,
          userId,
          accountId,
        })
      },
      async onBlockUser(userId) {
        await blockUser({ variables: { id: userId } })
        await refetch({ postId: router.query.postId })
      },
      async onDeletePost(postId) {
        await deletePost({
          variables: {
            postId: postId,
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsolePostDetail($postId: ID!, $pageInput: PageInput) {
    post(id: $postId) {
      ...PostBox
      replies(pageInput: $pageInput) {
        ...RepliesConnection
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      displayName
      avatar {
        uri
      }
    }
  }
  ${RepliesConnectionFragmentDoc}
  ${PostBoxFragmentDoc}
`

const DELETE_POST = gql`
  mutation DeletePost($postId: ID!) {
    deletePost(postId: $postId) {
      ...PostBox
    }
  }
  ${PostBoxFragmentDoc}
`
